import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadPlatformChatUnions } from '../../platform-chat-moderation/api/platformChatModerationApi.js';
import {
  loadPlatformUnionMembers,
  setPlatformUnionCommunityType,
  setPlatformUnionMemberRole,
  transferPlatformUnionLeadership,
} from '../api/platformUnionManagementApi.js';

export function usePlatformUnionManagement() {
  const [unions, setUnions] = useState([]);
  const [selectedUnionId, setSelectedUnionId] = useState('');
  const [members, setMembers] = useState([]);
  const [loadingUnions, setLoadingUnions] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [workingMembershipId, setWorkingMembershipId] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });

  const selectedUnion = useMemo(
    () => unions.find((union) => union.unionId === selectedUnionId) || null,
    [selectedUnionId, unions],
  );

  const refreshUnions = useCallback(async ({ preserveSelection = true } = {}) => {
    setLoadingUnions(true);
    try {
      const nextUnions = await loadPlatformChatUnions();
      setUnions(nextUnions);
      const currentUnion = preserveSelection
        ? nextUnions.find((union) => union.unionId === selectedUnionId)
        : null;
      const nextUnion = currentUnion || nextUnions[0] || null;
      setSelectedUnionId(nextUnion?.unionId || '');
      if (!nextUnion) setMembers([]);
      setNotice((current) => (current.type === 'success' ? current : { type: '', message: '' }));
      return nextUnions;
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '관리 가능한 유니온 목록을 불러오지 못했습니다.',
      });
      return [];
    } finally {
      setLoadingUnions(false);
    }
  }, [selectedUnionId]);

  const refreshMembers = useCallback(async (unionId = selectedUnionId) => {
    if (!unionId) {
      setMembers([]);
      return [];
    }
    setLoadingMembers(true);
    try {
      const nextMembers = await loadPlatformUnionMembers(unionId);
      setMembers(nextMembers);
      setNotice((current) => (current.type === 'success' ? current : { type: '', message: '' }));
      return nextMembers;
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '유니온 멤버 목록을 불러오지 못했습니다.',
      });
      return [];
    } finally {
      setLoadingMembers(false);
    }
  }, [selectedUnionId]);

  useEffect(() => {
    void refreshUnions({ preserveSelection: false });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedUnionId) return;
    void refreshMembers(selectedUnionId);
  }, [refreshMembers, selectedUnionId]);

  const selectUnion = useCallback((unionId) => {
    setSelectedUnionId(String(unionId || ''));
    setNotice({ type: '', message: '' });
  }, []);

  const setMemberRole = useCallback(async ({ membershipId, role }) => {
    if (!membershipId || workingMembershipId || !selectedUnionId) return false;
    setWorkingMembershipId(membershipId);
    setNotice({ type: '', message: '' });
    try {
      await setPlatformUnionMemberRole({ unionId: selectedUnionId, membershipId, role });
      await refreshMembers(selectedUnionId);
      setNotice({ type: 'success', message: role === 'deputy' ? '파딱으로 지정했습니다.' : '파딱을 해제했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '멤버 역할을 변경하지 못했습니다.' });
      return false;
    } finally {
      setWorkingMembershipId('');
    }
  }, [refreshMembers, selectedUnionId, workingMembershipId]);

  const transferLeadership = useCallback(async (membershipId) => {
    if (!membershipId || workingMembershipId || !selectedUnionId) return false;
    setWorkingMembershipId(membershipId);
    setNotice({ type: '', message: '' });
    try {
      await transferPlatformUnionLeadership({ unionId: selectedUnionId, membershipId });
      await refreshMembers(selectedUnionId);
      setNotice({ type: 'success', message: '주딱 권한을 이전했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '주딱 권한을 이전하지 못했습니다.' });
      return false;
    } finally {
      setWorkingMembershipId('');
    }
  }, [refreshMembers, selectedUnionId, workingMembershipId]);

  const setCommunityType = useCallback(async (communityType) => {
    if (!selectedUnionId) return false;
    setNotice({ type: '', message: '' });
    try {
      await setPlatformUnionCommunityType({ unionId: selectedUnionId, communityType });
      await refreshUnions({ preserveSelection: true });
      setNotice({ type: 'success', message: '커뮤니티 유형을 변경했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '커뮤니티 유형을 변경하지 못했습니다.' });
      return false;
    }
  }, [refreshUnions, selectedUnionId]);

  return {
    unions,
    selectedUnion,
    selectedUnionId,
    members,
    loadingUnions,
    loadingMembers,
    workingMembershipId,
    notice,
    selectUnion,
    refreshUnions,
    refreshMembers,
    setMemberRole,
    transferLeadership,
    setCommunityType,
  };
}
